import type { ChangeLogValue } from './interfaces';

/**
 * Identifiers and bookkeeping a change request row carries next to the real fields, never shown in the
 * log. `regionId` and `bdmId` travel with the region and BDM names, which say the same thing readably.
 */
const EXCLUDED_FIELDS = new Set([
  'lockedByActionId',
  'id',
  'ohpenId',
  'oldBrsId',
  'oldFirmId',
  'firmOhpenId',
  'regionId',
  'bdmId',
  'assistantId',
  'adviserId',
]);

/** Objects whose history is not shown at all: an intermediary's home and regional office addresses. */
const EXCLUDED_OBJECTS = new Set(['homeAddress', 'regionalOfficeAddress']);

/** Where one changed field sits in the change request grid. */
export type FieldPath = {
  /** The grid's column key, `<collection>.<column>` for a field of a collection. */
  field: string;
  collection?: string;
  /** The collection item's own key, as the backend names it inside the brackets. */
  itemKey?: string;
  excluded: boolean;
};

const COLLECTION_PATH = /^(\w+)\[([^\]]*)\]\.(.+)$/;

const leafOf = (path: string): string => path.slice(path.lastIndexOf('.') + 1);

/**
 * Maps the backend's path to a changed field onto the column the change request grid shows it in. The
 * grid flattens one level of nesting (`contactDetails.email` is `email`) and a collection item's fields
 * (`tradingAddresses[{id}].city` is `tradingAddresses.city`). Deeper paths keep everything below the
 * first object, so `personalData.homeAddress.city` stays apart from `personalData.regionalOfficeAddress.city`.
 * The firm's own address is flattened into `firmAddress<Part>` columns.
 */
export const parseFieldPath = (path: string): FieldPath => {
  const excluded = EXCLUDED_FIELDS.has(leafOf(path)) || path.split('.').some(segment => EXCLUDED_OBJECTS.has(segment));

  const match = COLLECTION_PATH.exec(path);
  if (match) {
    const [, collection, itemKey, rest] = match;

    return { field: `${collection}.${leafOf(rest)}`, collection, itemKey, excluded };
  }

  const segments = path.split('.');
  if (segments[0] === 'firmAddress' && segments.length === 2) {
    const part = segments[1];

    return { field: `firmAddress${part.charAt(0).toUpperCase()}${part.slice(1)}`, excluded };
  }

  return { field: segments.length > 2 ? segments.slice(1).join('.') : leafOf(path), excluded };
};

/** A JSON value as the log holds it. Codes and timestamps stay raw: the drawer formats them per column. */
export const formatValue = (value: unknown): ChangeLogValue => {
  if (value === null || value === undefined || value === '') return null;

  if (Array.isArray(value)) {
    const items = value.flatMap(item => formatValue(item) ?? []);

    return items.length ? items : null;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const label = record.displayName ?? record.name ?? record.value ?? record.key ?? record.id;

    return label === undefined ? null : formatValue(label);
  }

  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  return null;
};
